<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SecretCreatedModal from './SecretCreatedModal.vue';

const AVAILABLE_EVENTS = ['sent', 'delivered', 'read', 'failed', 'replied'];

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    SecretCreatedModal,
  },
  data() {
    return {
      name: '',
      url: '',
      selectedEvents: [...AVAILABLE_EVENTS],
      showSecretModal: false,
      createdWebhook: null,
      availableEvents: AVAILABLE_EVENTS,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'kaptionDeliveryWebhooks/getUIFlags',
    }),
    isFormValid() {
      return (
        this.name.trim() !== '' &&
        this.url.trim() !== '' &&
        this.url.startsWith('https://') &&
        this.selectedEvents.length > 0
      );
    },
  },
  methods: {
    toggleEvent(event) {
      const index = this.selectedEvents.indexOf(event);
      if (index > -1) {
        this.selectedEvents.splice(index, 1);
      } else {
        this.selectedEvents.push(event);
      }
    },
    navigateBack() {
      this.$router.push({
        name: 'settings_kaption_delivery_webhooks',
      });
    },
    hideSecretModal() {
      this.showSecretModal = false;
      this.navigateBack();
    },
    async createWebhook() {
      if (!this.isFormValid) return;

      try {
        const response = await this.$store.dispatch(
          'kaptionDeliveryWebhooks/create',
          {
            kaption_delivery_webhook: {
              name: this.name.trim(),
              url: this.url.trim(),
              events: this.selectedEvents,
            },
          }
        );

        this.createdWebhook = response;
        this.showSecretModal = true;

        useAlert(this.$t('KAPTION_DELIVERY_WEBHOOKS.ADD.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('KAPTION_DELIVERY_WEBHOOKS.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_DELIVERY_WEBHOOKS.ADD.TITLE')"
        :description="$t('KAPTION_DELIVERY_WEBHOOKS.ADD.DESCRIPTION')"
        :back-button-label="$t('KAPTION_DELIVERY_WEBHOOKS.HEADER')"
        show-back-button
        @back="navigateBack"
      />
    </template>
    <template #body>
      <form class="max-w-2xl space-y-6" @submit.prevent="createWebhook">
        <!-- Webhook Name -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_DELIVERY_WEBHOOKS.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="name"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            :placeholder="$t('KAPTION_DELIVERY_WEBHOOKS.FORM.NAME.PLACEHOLDER')"
          />
        </div>

        <!-- Webhook URL -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_DELIVERY_WEBHOOKS.FORM.URL.LABEL') }}
          </label>
          <input
            v-model="url"
            type="url"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            :placeholder="$t('KAPTION_DELIVERY_WEBHOOKS.FORM.URL.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_DELIVERY_WEBHOOKS.FORM.URL.HELP') }}
          </p>
        </div>

        <!-- Event Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_DELIVERY_WEBHOOKS.FORM.EVENTS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-3">
            <label
              v-for="event in availableEvents"
              :key="event"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedEvents.includes(event)"
                class="rounded border-n-weak text-woot-500 focus:ring-woot-500"
                @change="toggleEvent(event)"
              />
              <span class="text-sm text-n-slate-12 capitalize">
                {{ event }}
              </span>
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_DELIVERY_WEBHOOKS.FORM.EVENTS.HELP') }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <NextButton
            blue
            :label="$t('KAPTION_DELIVERY_WEBHOOKS.FORM.SUBMIT')"
            :disabled="!isFormValid"
            :is-loading="uiFlags.creatingItem"
            type="submit"
          />
          <NextButton
            slate
            faded
            :label="$t('KAPTION_DELIVERY_WEBHOOKS.FORM.CANCEL')"
            @click="navigateBack"
          />
        </div>
      </form>
    </template>

    <SecretCreatedModal
      v-if="showSecretModal"
      :webhook="createdWebhook"
      @close="hideSecretModal"
    />
  </SettingsLayout>
</template>
